// Evaluacion de visibilidad de grupos por logica condicionada en el llenado de formularios.
//
// Un grupo con `visibility_rules` solo debe mostrarse cuando la pregunta disparadora
// fue efectivamente respondida y su puntaje coincide con `depends_on_value`.
//
// El bug historico (TKT-2354): la comparacion se hacia contra `obtained_points`, que por
// defecto vale 0 en una pregunta sin responder. Como la opcion "No" tambien vale 0, cualquier
// grupo condicionado a un valor 0 aparecia de entrada. El gate `isAnswered` lo evita:
// no se da una regla por cumplida si la pregunta disparadora aun no tiene respuesta.

/**
 * @param {Object} item        Item candidato a mostrarse.
 * @param {Array}  allItems    Todos los items del formulario.
 * @param {Function} getObtainedPoints  (q) => puntaje obtenido del item q.
 * @param {Function} isAnswered (q) => true si el item q fue efectivamente respondido.
 * @returns {boolean}          true si el item debe mostrarse.
 */
export function isItemVisibleByLogic (item, allItems, getObtainedPoints, isAnswered) {
  const rules = item.group?.visibility_rules
  if (!rules || rules.length === 0) return true

  // `rule.group_id` identifica al grupo condicionado (el mismo que `item.group.id`), no aporta nada
  // a la busqueda de la pregunta disparadora: esta se resuelve solo por `depends_on_item_id`.
  return rules.some((rule) => {
    return allItems.some((q) => {
      if (rule.depends_on_item_id !== q.id) return false
      if (!isAnswered(q)) return false
      return rule.depends_on_value === String(getObtainedPoints(q))
    })
  })
}

/**
 * Una respuesta cuenta como dada solo si existe y trae un valor concreto.
 * Un `value` null / undefined / '' se trata como sin responder (asi un puntaje 0
 * por defecto no dispara reglas condicionadas al valor 0).
 *
 * Contrato implicito con `getItemObtainedPoints` (utils/scoring.js): un NA debe seguir contando
 * como respuesta dada (`isAnswerGiven` -> true) sin disparar una regla condicionada al valor 0.
 * Esto solo funciona porque `getItemObtainedPoints` devuelve el string `'na'` (no un numero) para
 * ese caso, asi que `String(getObtainedPoints(q)) === rule.depends_on_value` nunca compara `'na'`
 * contra `'0'`. Si `getItemObtainedPoints` alguna vez normaliza NA a un numero, este contrato se
 * rompe en silencio.
 * @param {Object} answer  Entrada de `occurrence_answers` para el item (o undefined).
 * @returns {boolean}
 */
export function isAnswerGiven (answer) {
  if (answer === undefined || answer === null) return false
  return answer.value !== null && answer.value !== undefined && answer.value !== ''
}
